<script setup lang="ts">
import { ref } from 'vue';
import api from '~/api';
import type { AxiosError } from 'axios';

const snackbar = useSnackbarStore();
const openEmailLoginForm = ref(false); // 控制 email 登录表单显示
const email = ref('');
const password = ref('');
const passwordVisible = ref(false);
const utils = useUtils();
const router = useRouter();
const route = useRoute();
const localePath = useLocalePath();
const globalLoading = useLoadingStore();
const { t } = useI18n();

// 登录逻辑
const onLogin = async (event: Event) => {
    event.preventDefault(); // 阻止默认表单提交
    if (email.value && password.value) {
        try {
            globalLoading.startLoading();
            const resp = await api.loginByEmail(email.value, password.value);
            if (resp) {
                const redirectPath = route.query.redirect as string;
                if (redirectPath) {
                    await utils.loginByToken(resp.token, redirectPath);
                } else {
                    await utils.loginByToken(resp.token);
                }
            } else {
                console.error('Login failed.');
            }
        } catch (error) {
            snackbar.showError(error.response?.data?.message || error.response?.message || 'unknown error');
        } finally {
            globalLoading.stopLoading();
        }

    } else {
        console.error('Email and password are required.');
    }
};

// 切换密码可见性
const togglePasswordVisibility = () => {
    passwordVisible.value = !passwordVisible.value;
};
</script>

<template>
    <div class="w-full">
        <v-btn v-if="!openEmailLoginForm" @click="openEmailLoginForm = !openEmailLoginForm" class="w-full bg-white/10 text-white max-w-[425px] py-3 rounded-full text-lg font-semibold my-5">
            <img src="/public/icon/email.svg" class="h-6 w-6 mr-2" alt="Email Icon" />
            {{ t('common.continue_email') }}
        </v-btn>

        <v-expand-transition>
            <form v-if="openEmailLoginForm" @submit="onLogin" class="w-full">
                <!-- Email 输入框 -->
                <v-text-field v-model="email" :label="t('common.forms.labels.email')" outlined dense hide-details autocomplete="username" class="mb-4"></v-text-field>

                <!-- 密码输入框 -->
                <v-text-field v-model="password" :type="passwordVisible ? 'text' : 'password'" :label="t('common.forms.labels.password')" outlined dense hide-details autocomplete="current-password" class="mb-4" append-inner-icon="mdi-eye-off" @click:append-inner="togglePasswordVisibility"></v-text-field>

                <!-- 登录按钮 -->
                <v-btn type="submit" class="w-full bg-orange-600 text-neutral-950 h-12 rounded-full text-lg font-semibold my-4">
                    {{ t('common.login') }}
                </v-btn>

                <!-- 忘记密码链接 -->
                <div class="text-white/60 text-base text-center font-medium underline cursor-pointer flex justify-between">
                    <span class="text-orange-600 text-sm font-medium underline cursor-pointer" @click="router.push(localePath('/signup'))">{{ t('common.signup') }}</span>
                    <span @click="router.push(localePath('/password-reset'))">{{ t('common.login_forgot') }}</span>
                </div>

            </form>
        </v-expand-transition>
    </div>
</template>
<style lang="scss" scoped>
/* 取消自动填充的背景颜色 */
input:-webkit-autofill,
textarea:-webkit-autofill,
select:-webkit-autofill {
    box-shadow: 0 0 0px 1000px white inset !important;
    /* 设置白色背景 */
    -webkit-text-fill-color: inherit !important;
    /* 使用继承的文字颜色 */
    transition: background-color 5000s ease-in-out 0s !important;
    /* 避免颜色闪烁 */
}

/* 覆盖内置的背景色 */
.custom-input input {
    background-color: red !important;
    /* 防止背景干扰 */
    color: inherit;
}
</style>